import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import ReactSlider from 'react-slider'
import axiosInstance from '../utils/axiosInstance'
import { usePagination, useStoreProducts } from '../hooks/storeProducts'
import { useTranslation } from 'react-i18next'
import ProductCard from './ProductCard'
import { useCategoriesStore } from '../stores/categoriesStore'
import { useSearchStore } from '../stores/searchStore'
import Pagination from './Pagination'
import LoadingSpinner from './LoadingSpinner'
import { useMoney } from '../stores/currenciesStore'

const ShopSection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const productsRef = useRef(null);
    
    const [grid, setGrid] = useState(false)
    const [active, setActive] = useState(false)
    const [order, setOrder] = useState(null)
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [priceRange, setPriceRange] = useState([0, 100]);
    const [sliderRange, setSliderRange] = useState([0, 100]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    const {money} = useMoney()
    
    // Check if device is mobile
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        // Initial check
        checkIfMobile();
        
        // Add event listener
        window.addEventListener('resize', checkIfMobile);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);
    
    let sidebarController = () => {
        setActive(!active)
    }

    const {pagination, filters, setFilters, fetchData, extraData, loading} = usePagination('/api/products-for-store', 10, false)
    const categories = useCategoriesStore((state) => state.categories)
    const selectedCategoryId = useCategoriesStore((state) => state.selectedCategoryId)
    const globalHandleCategoryClick = useCategoriesStore((state) => state.handleCategoryClick)
    const clearSelectedCategory = useCategoriesStore((state) => state.clearSelectedCategory)
    const { searchText, clearSearch, setSearchText } = useSearchStore()

    const {t} = useTranslation()
    
    // Function to categorize products based on text_rank
    const categorizeProducts = (products) => {
        const exactMatches = [];
        const relatedResults = [];
        let hasAnyTextRank = false;
        
        products.forEach(product => {
            // Check if any product has a text_rank value
            if (product.text_rank !== null && product.text_rank !== undefined) {
                hasAnyTextRank = true;
                
                // Check if text_rank exists and is greater than 0.001
                if (product.text_rank > 0.001) {
                    exactMatches.push(product);
                } else {
                    relatedResults.push(product);
                }
            } else {
                relatedResults.push(product);
            }
        });
        
        return { 
            exactMatches, 
            relatedResults, 
            shouldSeparate: hasAnyTextRank // Only separate if at least one product has a text_rank value
        };
    };
    
    // Apply URL parameters to filters on initial load
    useEffect(() => {
        if(isInitialLoad) {
            const searchParams = new URLSearchParams(location.search);
            
            // Handle category filter from URL
            if(searchParams.get('category')) {
                const categoryId = parseInt(searchParams.get('category'), 10);
                filters.category = categoryId;
                setSelectedCategory(categoryId);
            }

            // Handle search filter from URL
            if(searchParams.get('search')) {
                filters.search = searchParams.get('search');
                setSearchText(searchParams.get('search'))
            }

            // Handle price filter from URL
            setIsInitialLoad(false)
            fetchData()
        }
    }, [])
    
    // Sync filters to URL parameters whenever filters change
    useEffect(() => {
        const searchParams = new URLSearchParams();
        if(filters.category) {
            searchParams.set('category', filters.category);
        }
        if(filters.search) {
            searchParams.set('search', filters.search);
        }

        navigate(`?${searchParams.toString()}`, {replace: true})
        
    }, [filters])

    useEffect(() => {
        if(extraData) {
            if(extraData.min && extraData.max) {
                if(extraData.min !== sliderRange[0] || extraData.max !== sliderRange[1]) {
                    setSliderRange([extraData.min, extraData.max]);
                    setPriceRange([extraData.min, extraData.max]);
                }
            }
        }
    }, [extraData])
    
    // Update local state when selectedCategoryId changes in the store
    useEffect(() => {
        if(!isInitialLoad) {
            setSelectedCategory(selectedCategoryId);
            
            // Apply filter if category is selected but not in filters
            if (selectedCategoryId && (!filters.category || filters.category !== selectedCategoryId)) {
                filters.category = selectedCategoryId;
            }
            // Remove filter if category is unselected but still in filters
            else if (!selectedCategoryId && filters.category) {
                delete filters.category;
            }
            fetchData()
        }
    }, [selectedCategoryId]);

    useEffect(() => {
        if(!isInitialLoad) {
            filters.search = searchText
            fetchData()
        }

        return () => {
            clearSearch()
        }

    }, [searchText])

    useEffect(() => {
        // This cleanup function will run when the component unmounts
        return () => {
            clearSelectedCategory();
        };
    }, [clearSelectedCategory]);

    // Local handler that uses the global handler with local state
    const handleCategoryClick = (categoryId) => {
        globalHandleCategoryClick(categoryId, filters, setFilters, setSelectedCategory);
    }
    
    // Handle price range changes
    const handlePriceRangeChange = (value) => {
        setPriceRange(value);
    }
    
    // Apply price filter
    const applyPriceFilter = () => {
        filters.min = priceRange[0]
        filters.max = priceRange[1]
        fetchData()
    }

    const handleOrderChange = (e) => {
        setOrder(e.target.value)
        filters.order = e.target.value
        fetchData()
    }

    const scrollToProductsIfNeeded = () => {
        if (productsRef.current) {
            const rect = productsRef.current.getBoundingClientRect();
            const offset = 100; // Add 100px offset from the top
            // Only scroll if the top of products section is not visible
            if (rect.top < offset) {
                window.scrollTo({
                    top: window.scrollY + rect.top - offset,
                    behavior: 'smooth'
                });
            }
        }
    }

    const handlePageChange = (pageNum) => {
        scrollToProductsIfNeeded()
        filters.page = pageNum
        fetchData()
    }

    const handleNextPage = () => {
        scrollToProductsIfNeeded()
        filters.page = pagination.current_page + 1
        fetchData()
    }

    const handlePreviousPage = () => {
        scrollToProductsIfNeeded()
        filters.page = pagination.current_page - 1
        fetchData()
    }

    return (
        <section className="shop py-80">
            <div className={`side-overlay ${active && "show"}`}></div>
            <div className="container container-lg">
                <div className="row">
                    {/* Sidebar Start */}
                    <div className="col-lg-3">
                        <div className={`shop-sidebar ${active && "active"}`}>
                            <button onClick={sidebarController}
                                type="button"
                                className="w-32 h-32 mt-8 border border-gray-100 shop-sidebar__close d-lg-none d-flex flex-center rounded-circle hover-bg-main-600 position-absolute inset-inline-end-0 me-10 hover-text-white hover-border-main-600"
                            >
                                <i className="ph ph-x" />
                            </button>
                            <div className="p-32 mb-32 border border-gray-100 shop-sidebar__box rounded-8">
                                <h6 className="pb-24 mb-24 text-xl border-gray-100 border-bottom">
                                    {t('categories.title')}
                                </h6>
                                <ul className="overflow-y-auto max-h-540 scroll-sm">
                                    <li className='mb-24'>
                                        <span 
                                            onClick={() => handleCategoryClick(null)}
                                            className={`cursor-pointer ${selectedCategory === null ? 'text-main-600' : 'text-gray-900 hover-text-main-600'}`}
                                        >
                                            {t('categories.all')}
                                        </span>
                                    </li>
                                    {
                                        categories?.length > 0 && categories.map(c => {
                                            return (<li className='mb-24' key={'category-'+c.id}>
                                                <span 
                                                    onClick={() => handleCategoryClick(c.id)}
                                                    className={`cursor-pointer ${selectedCategory === c.id ? 'text-main-600' : 'text-gray-900 hover-text-main-600'}`}
                                                >
                                                    {c.name}
                                                </span>
                                            </li>)
                                        })
                                    }
                                </ul>
                            </div>
                            <div className="p-32 mb-32 border border-gray-100 shop-sidebar__box rounded-8">
                                <h6 className="pb-24 mb-24 text-xl border-gray-100 border-bottom">
                                    {t('shop.filter_by_price')}
                                </h6>
                                <div className="custom--range">
                                    <ReactSlider
                                        className="horizontal-slider"
                                        thumbClassName="example-thumb"
                                        trackClassName="example-track"
                                        value={priceRange}
                                        min={sliderRange[0]}
                                        max={sliderRange[1]}
                                        onChange={handlePriceRangeChange}
                                        ariaLabel={['Lower thumb', 'Upper thumb']}
                                        ariaValuetext={state => `Thumb value ${state.valueNow}`}
                                        getAriaValueText={value => money(value)}
                                        renderThumb={(props, state) => {
                                            const { key, ...restProps } = props;
                                            return <div className='tw-w-fit' {...restProps} key={state.index}>{money(state.valueNow)}</div>;
                                        }}
                                        pearling
                                        minDistance={10}
                                    />
                                    <br />
                                    <br />
                                    <div className="flex-wrap-reverse gap-8 mt-24 flex-between">
                                        <button 
                                            type="button" 
                                            className="h-40 btn btn-main flex-align"
                                            onClick={applyPriceFilter}
                                        >
                                            {t('shop.filter')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Sidebar End */}
                    {/* Content Start */}
                    <div className="col-lg-9">
                        {/* Top Start */}
                        <div className="flex-wrap gap-16 mb-40 flex-between" ref={productsRef}>
                            <span className="text-gray-900">
                                {searchText && (
                                    <div className="mb-3">
                                        {t('shop.searching_for')}: <strong>{searchText}</strong>
                                    </div>
                                )}
                                {t('pagination.showing')} {pagination.from}-{pagination.to} {t('pagination.of')} {pagination.total} {t('pagination.results')}
                            </span>
                            <div className="flex-wrap gap-16 position-relative flex-align">
                                {pagination.data.length >= 4 && (
                                    <div className="gap-16 list-grid-btns flex-align">
                                        <button onClick={() => setGrid(true)}
                                            type="button"
                                            className={`w-44 h-44 flex-center border rounded-6 text-2xl list-btn border-gray-100 ${grid === true && "border-main-600 text-white bg-main-600"}`}
                                        >
                                            <i className="ph-bold ph-list-dashes" />
                                        </button>
                                        <button onClick={() => setGrid(false)}
                                            type="button"
                                            className={`w-44 h-44 flex-center border rounded-6 text-2xl grid-btn border-gray-100 ${grid === false && "border-main-600 text-white bg-main-600"}`}
                                        >
                                            <i className="ph ph-squares-four" />
                                        </button>
                                    </div>
                                )}
                                <div className="gap-4 text-gray-500 position-relative flex-align text-14">
                                    <label htmlFor="sorting" className="flex-shrink-0 text-inherit">
                                        {t('shop.sort_by')}:
                                    </label>
                                    <select 
                                        defaultValue={order}
                                        className="w-auto form-control common-input px-14 py-14 text-inherit rounded-6"
                                        id="sorting"
                                        onChange={handleOrderChange}
                                    >
                                        <option value={null}>{t('shop.default')}</option>
                                        <option value={'latest'}>{t('shop.latest')}</option>
                                        <option value={'cheapest'}>{t('shop.cheapest')}</option>
                                        <option value={'expensive'}>{t('shop.expensive')}</option>
                                    </select>
                                </div>
                                <button onClick={sidebarController}
                                    type="button"
                                    className="text-2xl border border-gray-100 w-44 h-44 d-lg-none d-flex flex-center rounded-6 sidebar-btn"
                                >
                                    <i className="ph-bold ph-funnel" />
                                </button>
                            </div>
                        </div>
                        {/* Top End */}
                        {loading ? (
                            <div className="gap-16 flex-center flex-column py-80">
                                <LoadingSpinner width={48} height={48} />
                                <p className="text-gray-500">{t('common.loading')}</p>
                            </div>
                        ) : pagination.data.length > 0 ? (
                            (() => {
                                const { exactMatches, relatedResults, shouldSeparate } = categorizeProducts(pagination.data);
                                return (
                                    <>
                                        {shouldSeparate ? (
                                            // If we should separate products (at least one has text_rank)
                                            <>
                                                {/* Exact matches */}
                                                {exactMatches.length > 0 ? (
                                                    // Render exact matches grid wrapper
                                                    <div className={`list-grid-wrapper ${grid && "list-view"}`} style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: `repeat(${grid ? 2 : isMobile ? 2 : 4}, minmax(170px, 1fr))`,
                                                        gap: '24px',
                                                        justifyContent: 'left'
                                                    }}>
                                                        {exactMatches.map(product => (
                                                            <ProductCard 
                                                                key={product.slug}
                                                                product={{ 
                                                                    id: product.id,
                                                                    slug: product.slug,
                                                                    name: product.name,
                                                                    price: product.price,
                                                                    computed_price: product.computed_price,
                                                                    images: product.images
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    // No exact matches message without grid wrapper
                                                    <div className="py-16 text-center">
                                                        <p className="text-gray-500">{t('shop.no_exact_matches')}</p>
                                                    </div>
                                                )}
                                                
                                                {/* Divider between exact and related results */}
                                                {relatedResults.length > 0 && (
                                                    <div className="mt-24 mb-16">
                                                        <h4 className="text-xl text-center fw-semibold">
                                                            {t('shop.might_interest_you')}
                                                        </h4>
                                                        <hr className="my-16 border-gray-100" />
                                                    </div>
                                                )}
                                                
                                                {/* Related results grid wrapper */}
                                                {relatedResults.length > 0 && (
                                                    <div className={`list-grid-wrapper ${grid && "list-view"}`} style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: `repeat(${grid ? 2 : isMobile ? 2 : 4}, minmax(170px, 1fr))`,
                                                        gap: '24px',
                                                        justifyContent: 'left'
                                                    }}>
                                                        {relatedResults.map(product => (
                                                            <ProductCard 
                                                                key={product.slug}
                                                                product={{ 
                                                                    id: product.id,
                                                                    slug: product.slug,
                                                                    name: product.name,
                                                                    price: product.price,
                                                                    computed_price: product.computed_price,
                                                                    images: product.images
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            // If we shouldn't separate products (all have null text_rank)
                                            // Render all products in a single grid
                                            <div className={`list-grid-wrapper ${grid && "list-view"}`} style={{
                                                display: 'grid',
                                                gridTemplateColumns: `repeat(${grid ? 2 : isMobile ? 2 : 4}, minmax(170px, 1fr))`,
                                                gap: '24px',
                                                justifyContent: 'left'
                                            }}>
                                                {pagination.data.map(product => (
                                                    <ProductCard 
                                                        key={product.slug}
                                                        product={{ 
                                                            id: product.id,
                                                            slug: product.slug,
                                                            name: product.name,
                                                            price: product.price,
                                                            computed_price: product.computed_price,
                                                            images: product.images
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                );
                            })()
                        ) : (
                            <div className="text-center py-80">
                                <p className="text-gray-500">{t('shop.no_products_found')}</p>
                            </div>
                        )}
                        {/* Pagination Start */}
                        {pagination.data.length > 0 && (
                            <Pagination 
                                pagination={pagination} 
                                onPage={(pageNum) => handlePageChange(pageNum)}
                                onNextPage={handleNextPage}
                                onPreviousPage={handlePreviousPage}
                            />
                        )}
                        {/* Pagination End */}
                    </div>
                    {/* Content End */}
                </div>
            </div>
        </section>
    )
}

export default ShopSection